import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { ChevronDown, Sparkles } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { paintChrome } from '@/shared/lib/browser-chrome';
import { useUrlParam } from '@/shared/lib/useUrlParam';
import { useRevealController } from '../model/useRevealController';
import { useRevealStore } from '../model/revealStore';
import { AIAssistantReveal } from './AIAssistantReveal';
import styles from './RevealController.module.css';

export interface RevealControllerProps {
  enabled: boolean;
  children: React.ReactNode;
}

const TEASER_ENABLED = false;

export const RevealController: React.FC<RevealControllerProps> = ({ enabled, children }) => {
  const { rootRef, skyRef, shellRef, state, open, close, onTransitionEnd } = useRevealController(enabled);

  const sync = useRevealStore((s) => s.sync);
  useEffect(() => {
    sync({ enabled, state, open, close });
  }, [enabled, state, open, close, sync]);

  const [aiParam, setAiParam] = useUrlParam<'' | '1'>('ai', '');
  const restored = useRef(false);
  useEffect(() => {
    if (!enabled || restored.current || aiParam !== '1') return;
    restored.current = true;
    open();
  }, [enabled, aiParam, open]);
  const wantParam = state === 'open' || state === 'opening' ? '1' : '';
  useEffect(() => {
    if (!enabled || aiParam === wantParam) return;
    setAiParam(wantParam);
  }, [enabled, aiParam, wantParam, setAiParam]);

  const { pathname } = useLocation();
  const lastPath = useRef(pathname);
  useEffect(() => {
    if (lastPath.current === pathname) return;
    lastPath.current = pathname;
    if (state !== 'collapsed') close();
  }, [pathname, state, close]);

  useEffect(() => {
    const el = rootRef.current;
    if (!enabled || !el || state === 'collapsed') {
      paintChrome('ai', null);
      return;
    }
    const paint = () => {
      const cs = getComputedStyle(el);
      paintChrome('ai', {
        top: cs.getPropertyValue('--ai-sky-top').trim(),
        bar: cs.getPropertyValue('--ai-sky-bot').trim(),
      });
    };
    paint();
    const mo = new MutationObserver(paint);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => {
      mo.disconnect();
      paintChrome('ai', null);
    };
  }, [enabled, state, rootRef]);

  useEffect(() => {
    shellRef.current?.toggleAttribute('inert', enabled && state !== 'collapsed');
  }, [enabled, state, shellRef]);

  return (
    <div
      ref={rootRef}
      className={cn(styles.root, !enabled && styles.rootIdle)}
      data-state={enabled ? state : undefined}
      onTransitionEnd={enabled ? onTransitionEnd : undefined}
    >
      <div ref={shellRef} className={cn(styles.shell, !enabled && styles.shellIdle)}>
        {children}
      </div>

      {!enabled ? null : (
        <div ref={skyRef} className={styles.sky}>
      <AIAssistantReveal state={state} onClose={close} />

      { }
      {TEASER_ENABLED && state === 'collapsed' && (
        <div className={styles.teaserGate} aria-hidden>
          <div className={styles.teaser}>
            <div className={styles.teaserPill} onClick={open}>
              <span className={styles.teaserBubble}>
                <Sparkles size={13} />
              </span>
              <span className={styles.teaserLabel}>Попробуйте AI режим</span>
            </div>
            <ChevronDown className={styles.teaserArrow} size={16} />
          </div>
        </div>
      )}

      { }
      <div className={styles.hint} aria-hidden>
        <span className={styles.hintText}>Потяните вниз, чтобы перейти в режим AI</span>
        <ChevronDown className={styles.hintArrow} size={22} />
      </div>

      { }
      <div className={styles.blobs} aria-hidden>
        <span className={cn(styles.blob, styles.blob1)} />
        <span className={cn(styles.blob, styles.blob2)} />
        <span className={cn(styles.blob, styles.blob3)} />
      </div>

      { }
      <button
        type="button"
        className={styles.peek}
        aria-label="Открыть AI-помощника"
        aria-hidden={state !== 'collapsed'}
        tabIndex={state === 'collapsed' ? 0 : -1}
        style={{ pointerEvents: state === 'collapsed' ? 'auto' : 'none' }}
        onClick={open}
      />
        </div>
      )}
    </div>
  );
};
